import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import AdminPanel from '../../components/admin/AdminPanel';
import AdminBadge from '../../components/admin/AdminBadge';
import AdminTable from '../../components/admin/AdminTable';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import AddAgentModal from '../../components/admin/AddAgentModal';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  Users, UserPlus, Search, Eye, Calendar, DollarSign, AlertCircle,
  RefreshCw, BarChart3, TrendingUp
} from 'lucide-react';
import '../../styles/admin-theme.css';

const AgentManagement = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('firstname');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [refreshing, setRefreshing] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Check if user has agency admin access
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  const fetchAgents = useCallback(async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setFetchError(null);

      const filters = {
        status: filterStatus,
        role: filterRole,
        sortBy,
        sortOrder
      };
      
      // Only include search if searchQuery has a value
      if (searchQuery && searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await agencyAdminService.getAgents(filters);

      if (response.success && response.data) {
        setAgents(response.data);
        setLastUpdatedAt(new Date());
        // Update adminCount from summary if available
        if (response.summary && response.summary.adminCount !== undefined) {
          setAdminCount(response.summary.adminCount);
        }
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setFetchError('Unable to load agents. Please try again.');
      // Preserve previous agents list on error
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterStatus, filterRole, sortBy, sortOrder, searchQuery]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Debounced search refetch
  useEffect(() => {
    if (!hasAgencyAdminAccess()) return;
    
    const debounceTimer = setTimeout(() => {
      fetchAgents(true);
    }, 400);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Immediate refetch on filter/sort changes (except search, which is debounced)
  useEffect(() => {
    if (!hasAgencyAdminAccess()) return;
    fetchAgents(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterRole, sortBy, sortOrder]);

  const handleAgentCreated = () => {
    fetchAgents(true);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'inactive': return 'suspended';
      case 'pending': return 'pending';
      case 'denied': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Active';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getPerformanceBadgeVariant = (performance) => {
    switch (performance) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'needs_improvement': return 'error';
      default: return 'default';
    }
  };

  const formatMetric = (value, formatter) => {
    if (value === null || value === undefined) {
      return { display: '—', hasValue: false };
    }
    return { display: formatter(value), hasValue: true };
  };

  const tableColumns = [
    {
      key: 'name',
      header: 'Agent',
      render: (value, row) => (
        <div className="flex items-center">
          <div className="flex justify-center items-center mr-3 w-10 h-10 text-sm font-semibold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
            {(row.name || 'A').split(' ').map(n => n[0]).join('').substring(0, 2)}
          </div>
          <div>
            <div className="font-medium text-gray-900">{row.name || 'Unknown'}</div>
            <div className="text-sm text-gray-500">{row.email || '—'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <AdminBadge variant={getStatusBadgeVariant(value)} dot>
          {getStatusLabel(value)}
        </AdminBadge>
      )
    },
    {
      key: 'role',
      header: 'Role',
      render: (value) => <span className="text-sm text-gray-600">{value || '—'}</span>
    },
    {
      key: 'totalTrips',
      header: 'Trips',
      align: 'right',
      render: (value) => {
        const metric = formatMetric(value, (v) => v.toString());
        return (
          <span className="font-medium" title={!metric.hasValue ? 'Not yet available' : undefined}>
            {metric.display}
          </span>
        );
      }
    },
    {
      key: 'totalRevenue',
      header: 'Total Revenue',
      align: 'right',
      render: (value) => {
        const metric = formatMetric(value, (v) => `$${v.toLocaleString()}`);
        return (
          <span className="font-semibold" title={metric.hasValue ? 'Total commission earned' : 'Not yet available'}>
            {metric.display}
          </span>
        );
      }
    },
    {
      key: 'conversionRate',
      header: 'Conversion',
      align: 'right',
      render: (value) => {
        const metric = formatMetric(value, (v) => `${v.toFixed(1)}%`);
        return (
          <span className="font-medium" title={metric.hasValue ? 'Clients converted to customers' : 'Not yet available'}>
            {metric.display}
          </span>
        );
      }
    },
    {
      key: 'performance',
      header: 'Performance',
      render: (value) => {
        if (!value) {
          return <span className="text-sm text-gray-400">—</span>;
        }
        return (
          <AdminBadge variant={getPerformanceBadgeVariant(value)}>
            {value.replace('_', ' ')}
          </AdminBadge>
        );
      }
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (value, row) => (
        <div className="flex gap-2 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/agent/admin/agents/${row.id}`);
            }}
            className="p-2 text-gray-600 rounded-lg transition-colors hover:bg-gray-100"
            title="View details"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (!hasAgencyAdminAccess()) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Agent Management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4 admin-spinner"></div>
          <p className="text-gray-600">Loading agents...</p>
        </div>
      </div>
    );
  }

  const headerActions = [
    <button
      key="refresh"
      onClick={() => fetchAgents(true)}
      disabled={refreshing}
      className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
      Refresh
    </button>,
    <button
      key="add-agent"
      onClick={() => setShowAddAgentModal(true)}
      className="flex items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
    >
      <UserPlus className="mr-2 w-5 h-5" />
      Add New Agent
    </button>
  ];

  return (
    <>
      <AdminPageHeader
        title="Agent Management"
        subtitle={
          lastUpdatedAt
            ? `Last updated ${lastUpdatedAt.toLocaleTimeString()}`
            : "Manage your agency's agents and their performance"
        }
        actions={headerActions}
      />

      {/* Error Banner */}
      {fetchError && (
        <AdminPanel className="mb-6 bg-red-50 border-red-200">
          <div className="flex justify-between items-start">
            <div className="flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="mb-1 font-semibold text-red-900">Couldn't load agents</h3>
                <p className="text-sm text-red-700">{fetchError}</p>
              </div>
            </div>
            <button
              onClick={() => fetchAgents(true)}
              className="px-4 py-2 text-sm font-medium text-red-700 bg-white rounded-lg border border-red-300 transition-colors hover:bg-red-50"
            >
              Retry
            </button>
          </div>
        </AdminPanel>
      )}

      {/* Filters Panel */}
      <AdminPanel className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search agents by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="py-2 pr-4 pl-10 w-full text-sm bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="denied">Denied</option>
            </select>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="firstname-asc">Name A-Z</option>
              <option value="firstname-desc">Name Z-A</option>
              <option value="totalRevenue-desc">Revenue High-Low</option>
              <option value="totalRevenue-asc">Revenue Low-High</option>
              <option value="totalTrips-desc">Trips High-Low</option>
              <option value="conversionRate-desc">Conversion High-Low</option>
              <option value="createdat-desc">Created (Newest)</option>
              <option value="createdat-asc">Created (Oldest)</option>
            </select>
            <button 
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center px-3 py-2 text-sm bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
              title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
            >
              {viewMode === 'grid' ? <BarChart3 className="w-4 h-4" /> : <Users className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </AdminPanel>

      {/* Agents Display */}
      {viewMode === 'list' ? (
        <AdminPanel>
          <AdminTable
            columns={tableColumns}
            data={agents}
            onRowClick={(row) => navigate(`/agent/admin/agents/${row.id}`)}
            emptyState={
              agents.length === 0 ? (
                <AdminEmptyState
                  icon={Users}
                  title="Add your first team member"
                  description="You haven't added any agents yet. Add your first team member to start creating trips and itineraries. They'll receive an email to set their password."
                  action={
                    <button
                      onClick={() => setShowAddAgentModal(true)}
                      className="inline-flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                    >
                      <UserPlus className="mr-2 w-5 h-5" />
                      Add Agent
                    </button>
                  }
                />
              ) : (
                <AdminEmptyState
                  icon={Users}
                  title="No agents match your filters"
                  description="Try adjusting your search, status, or role filters."
                  action={
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setFilterStatus('all');
                          setFilterRole('all');
                        }}
                        className="inline-flex items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                      >
                        Clear filters
                      </button>
                    </div>
                  }
                />
              )
            }
          />
        </AdminPanel>
      ) : (
        <>
          {agents.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {agents.map((agent) => {
                const tripsMetric = formatMetric(agent.totalTrips, (v) => v.toString());
                const revenueMetric = formatMetric(agent.totalRevenue, (v) => `$${v.toLocaleString()}`);
                const conversionMetric = formatMetric(agent.conversionRate, (v) => `${v.toFixed(0)}%`);

                return (
                  <AdminPanel key={agent.id} variant="bordered" className="transition-shadow hover:shadow-md">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="flex flex-shrink-0 justify-center items-center w-9 h-9 text-xs font-semibold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                          {(agent.name || 'A').split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{agent.name || 'Unknown'}</h3>
                          <p className="text-xs text-gray-500 truncate">{agent.email || '—'}</p>
                        </div>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agent/admin/agents/${agent.id}`);
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 flex-shrink-0 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>

                    {/* Compact metrics row */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <Calendar className="w-3 h-3 text-blue-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900" title={!tripsMetric.hasValue ? 'Not yet available' : undefined}>
                          {tripsMetric.display}
                        </p>
                        <p className="text-xs text-gray-500">Trips</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <DollarSign className="w-3 h-3 text-green-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900" title={revenueMetric.hasValue ? 'Total commission earned' : 'Not yet available'}>
                          {revenueMetric.display}
                        </p>
                        <p className="text-xs text-gray-500">Revenue</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <TrendingUp className="w-3 h-3 text-purple-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900" title={conversionMetric.hasValue ? 'Clients converted to customers' : 'Not yet available'}>
                          {conversionMetric.display}
                        </p>
                        <p className="text-xs text-gray-500">Conv.</p>
                      </div>
                    </div>

                    {/* Performance badge and status */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      {agent.performance ? (
                        <AdminBadge variant={getPerformanceBadgeVariant(agent.performance)} size="sm">
                          {agent.performance.replace('_', ' ')}
                        </AdminBadge>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      <AdminBadge variant={getStatusBadgeVariant(agent.status)} size="sm" dot>
                        {getStatusLabel(agent.status)}
                      </AdminBadge>
                    </div>
                  </AdminPanel>
                );
              })}
            </div>
          ) : (
            agents.length === 0 ? (
              <AdminEmptyState
                icon={Users}
                title="Add your first team member"
                description="You haven't added any agents yet. Add your first team member to start creating trips and itineraries. They'll receive an email to set their password."
                action={
                  <button
                    onClick={() => setShowAddAgentModal(true)}
                    className="inline-flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                  >
                    <UserPlus className="mr-2 w-5 h-5" />
                    Add Agent
                  </button>
                }
              />
            ) : (
              <AdminEmptyState
                icon={Users}
                title="No agents match your filters"
                description="Try adjusting your search, status, or role filters."
                action={
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setFilterStatus('all');
                        setFilterRole('all');
                      }}
                      className="inline-flex items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                    >
                      Clear filters
                    </button>
                  </div>
                }
              />
            )
          )}
        </>
      )}

      {/* Add Agent Modal */}
      <AddAgentModal
        isOpen={showAddAgentModal}
        onClose={() => setShowAddAgentModal(false)}
        onAgentCreated={handleAgentCreated}
        adminCount={adminCount}
      />
    </>
  );
};

export default AgentManagement;
